import {
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useOnClickOutside } from "../../hooks";
import { DesignSystemProvider } from "../design-system-provider/design-system-provider";
import { dropdownBehaviourContentClass } from "./dropdown-behaviour.css";

export enum DropdownBehaviourHorizontalPositionEnum {
  Left = "Left",
  Right = "Right",
  Center = "Center",
}

export enum DropdownBehaviourVerticalPositionEnum {
  Top = "Top",
  Bottom = "Bottom",
}

type DropdownBehaviourProps = PropsWithChildren & {
  /**
   * Horizontal position of Dropdown
   */
  horizontalPosition?: DropdownBehaviourHorizontalPositionEnum;
  /**
   * Vertical position of Dropdown
   */
  verticalPosition?: DropdownBehaviourVerticalPositionEnum;
  /**
   * Is the Dropdown open/visible
   */
  isOpen: boolean;
  /**
   * Action to change open status
   */
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  /**
   * Is dropDown would have flex vertical position
   */
  isVerticalPositionFlexible?: boolean;
};

type DropdownPositionParams = {
  ref: RefObject<HTMLInputElement>;
  dropdownContentRef: RefObject<HTMLDivElement>;
  verticalPosition: DropdownBehaviourVerticalPositionEnum;
  horizontalPosition: DropdownBehaviourHorizontalPositionEnum;
  isVerticalPositionFlexible?: boolean;
};

const calculateDropdownPosition = ({
  ref,
  dropdownContentRef,
  verticalPosition,
  horizontalPosition,
  isVerticalPositionFlexible,
}: DropdownPositionParams) => {
  const dimensions = ref?.current?.getBoundingClientRect();

  if (!dimensions) {
    return { verticalPositionStyles: {}, horizontalPositionStyles: {} };
  }

  let currentVerticalPosition = verticalPosition;

  const scrollPosition = window.scrollY;
  const pageHeight = window.innerHeight + scrollPosition;
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const topPosition = dimensions.top;
  const rightPosition = dimensions.right;
  const leftPosition = dimensions.left;
  const bottomPosition = dimensions.bottom;
  const elementWidth = dimensions.width;
  const dropdownContentHeight =
    dropdownContentRef.current?.getBoundingClientRect().height;

  if (isVerticalPositionFlexible) {
    if (dropdownContentHeight && topPosition < dropdownContentHeight) {
      currentVerticalPosition = DropdownBehaviourVerticalPositionEnum.Bottom;
    } else if (
      dropdownContentHeight &&
      viewportHeight - bottomPosition < dropdownContentHeight
    ) {
      currentVerticalPosition = DropdownBehaviourVerticalPositionEnum.Top;
    }
  }

  // Calculate vertical styles
  let verticalPositionStyles = {};
  if (currentVerticalPosition === DropdownBehaviourVerticalPositionEnum.Top) {
    verticalPositionStyles = {
      bottom: `${pageHeight - scrollPosition - topPosition}px`,
    };
  } else if (
    currentVerticalPosition === DropdownBehaviourVerticalPositionEnum.Bottom
  ) {
    verticalPositionStyles = {
      top: `${scrollPosition + bottomPosition}px`,
    };
  }

  // Calculate horizontal position
  let horizontalPositionStyles;
  if (horizontalPosition === DropdownBehaviourHorizontalPositionEnum.Left) {
    horizontalPositionStyles = {
      left: `${leftPosition}px`,
    };
  } else if (
    horizontalPosition === DropdownBehaviourHorizontalPositionEnum.Right
  ) {
    horizontalPositionStyles = {
      right: `${viewportWidth - rightPosition}px`,
    };
  } else {
    horizontalPositionStyles = {
      left: `${leftPosition}px`,
      width: `${elementWidth}px`,
    };
  }

  return { verticalPositionStyles, horizontalPositionStyles };
};

export const DropdownBehaviour = forwardRef<
  HTMLInputElement,
  DropdownBehaviourProps
>(
  (
    {
      children,
      isOpen,
      setIsOpen,
      verticalPosition = DropdownBehaviourVerticalPositionEnum.Bottom,
      horizontalPosition = DropdownBehaviourHorizontalPositionEnum.Center,
      isVerticalPositionFlexible,
    }: DropdownBehaviourProps,
    ref
  ) => {
    let portalContainer = document.getElementById("dropdown-container");
    if (!portalContainer) {
      const portalElementContainer = document.createElement("div");
      portalElementContainer.setAttribute("id", "dropdown-container");
      document.body.appendChild(portalElementContainer);
      portalContainer = portalElementContainer;
    }
    portalContainer.setAttribute("role", "navigation");

    const dropdownBehaviourContent = useRef<HTMLDivElement>(null);

    const [verticalPositionStyles, setVerticalPositionStyles] = useState({});
    const [horizontalPositionStyles, setHorizontalPositionStyles] = useState(
      {}
    );

    const hideDropdown = () => {
      setIsOpen(false);
    };

    const placeDropdown = () => {
      const { verticalPositionStyles, horizontalPositionStyles } =
        calculateDropdownPosition({
          ref: ref as RefObject<HTMLInputElement>,
          dropdownContentRef: dropdownBehaviourContent,
          verticalPosition,
          horizontalPosition,
          isVerticalPositionFlexible,
        });

      setVerticalPositionStyles(verticalPositionStyles);
      setHorizontalPositionStyles(horizontalPositionStyles);
    };

    useOnClickOutside(
      dropdownBehaviourContent,
      hideDropdown,
      ref as RefObject<HTMLInputElement>
    );

    useEffect(() => {
      if (isOpen) {
        placeDropdown();
      }
    }, [isOpen, document.body.scrollHeight]);

    if (!isOpen) return null;

    return createPortal(
      <DesignSystemProvider>
        <div
          ref={dropdownBehaviourContent}
          style={{ ...verticalPositionStyles, ...horizontalPositionStyles }}
          className={dropdownBehaviourContentClass}
        >
          {children}
        </div>
      </DesignSystemProvider>,
      portalContainer
    );
  }
);

DropdownBehaviour.displayName = "DropdownBehaviour";
